import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';

/**
 * InfiniteScroll component for loading more content when scrolling
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Function} props.loadMore - Function to call when more items should be loaded
 * @param {boolean} props.hasMore - Whether there are more items to load
 * @param {boolean} props.isLoading - Whether data is currently loading
 * @param {number} props.threshold - Distance from the edge to trigger loading (in pixels)
 * @param {boolean} props.loadOnMount - Whether to load more items when component mounts
 * @param {boolean} props.inverse - Whether scrolling is inverted (for chat-like UIs)
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.loader - Custom loader component
 * @param {boolean} props.useWindowScroll - Whether to use window scroll instead of container scroll
 */
const InfiniteScroll = ({
  children,
  loadMore,
  hasMore = false,
  isLoading = false,
  threshold = 200,
  loadOnMount = false,
  inverse = false,
  className = '',
  loader = null,
  useWindowScroll = true
}) => {
  const { semantic } = useColors();
  const containerRef = useRef(null);
  const prevScrollHeight = useRef(0);
  const prevScrollTop = useRef(0);
  
  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!hasMore || isLoading) return;
    
    if (useWindowScroll) {
      if (inverse) {
        // For inverse scrolling (chat-like UIs), load more when scrolling up
        if (window.scrollY <= threshold) {
          // Save current scroll position and height
          prevScrollHeight.current = document.body.scrollHeight;
          prevScrollTop.current = window.scrollY;
          loadMore();
        }
      } else {
        // For normal scrolling, load more when scrolling down
        const scrollBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
        if (scrollBottom <= threshold) {
          loadMore();
        }
      }
    } else if (containerRef.current) {
      const container = containerRef.current;
      
      if (inverse) {
        // For inverse scrolling (chat-like UIs), load more when scrolling up
        if (container.scrollTop <= threshold) {
          // Save current scroll position and height
          prevScrollHeight.current = container.scrollHeight;
          prevScrollTop.current = container.scrollTop;
          loadMore();
        }
      } else {
        // For normal scrolling, load more when scrolling down
        const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (scrollBottom <= threshold) {
          loadMore();
        }
      }
    }
  }, [hasMore, isLoading, loadMore, threshold, inverse, useWindowScroll]);
  
  // Attach scroll event listener
  useEffect(() => {
    if (useWindowScroll) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      const container = containerRef.current;
      if (!container) return;
      
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, useWindowScroll]);
  
  // Load initial data if needed
  useEffect(() => {
    if (loadOnMount && hasMore && !isLoading) {
      loadMore();
    }
  }, [loadOnMount, hasMore, isLoading, loadMore]);
  
  // Maintain scroll position after loading more items in inverse mode
  useEffect(() => {
    if (inverse && !isLoading) {
      if (useWindowScroll) {
        const newScrollHeight = document.body.scrollHeight;
        const heightDifference = newScrollHeight - prevScrollHeight.current;
        
        if (heightDifference > 0 && prevScrollHeight.current > 0) {
          window.scrollTo(0, prevScrollTop.current + heightDifference);
        }
      } else if (containerRef.current) {
        const container = containerRef.current;
        const newScrollHeight = container.scrollHeight;
        const heightDifference = newScrollHeight - prevScrollHeight.current;
        
        if (heightDifference > 0 && prevScrollHeight.current > 0) {
          container.scrollTop = prevScrollTop.current + heightDifference;
        }
      }
    }
  }, [children, isLoading, inverse, useWindowScroll]);
  
  return (
    <div 
      ref={containerRef} 
      className={`${useWindowScroll ? '' : 'overflow-y-auto'} ${className}`}
    >
      {inverse && isLoading && loader}
      {children}
      {!inverse && isLoading && loader}
    </div>
  );
};

InfiniteScroll.propTypes = {
  children: PropTypes.node.isRequired,
  loadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  threshold: PropTypes.number,
  loadOnMount: PropTypes.bool,
  inverse: PropTypes.bool,
  className: PropTypes.string,
  loader: PropTypes.node,
  useWindowScroll: PropTypes.bool
};

export default InfiniteScroll;

